import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import {
    ordersSelector,
    useFormaliteService,
    DossierOpenSearch,
} from '@europrocurement/l2d-domain';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction } from '@reduxjs/toolkit';
import { DataSource } from '@europrocurement/l2d-redux-utils';
import { useParams } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useSnackbar } from 'notistack';
import { Order } from '@europrocurement/l2d-domain/reducers/formalites/slices/orderSlice';
import SelectedOrderContext from './SelectedOrderContext';

/**
 * Hook personnalisé pour récupérer les détails des dossiers via OpenSearch
 * Cette approche permet d'avoir des données plus complètes que celles de l'API MySQL
 */
const useOrderDossiers = (orderDossiers: Order['dossiers']) => {
    const [dossiers, setDossiers] = useState<Array<DossierOpenSearch>>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { selectOpenSearchDossier } = useFormaliteService();
    const dispatch = useDispatch();

    const fetchDossierDetails = useCallback(async () => {
        if (!orderDossiers || orderDossiers.length === 0) {
            setDossiers([]);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Utilisation de reduce() avec async/await pour traitement séquentiel
            const fetchedDossiers = await orderDossiers.reduce(
                async (accPromise, orderDossier) => {
                    const acc = await accPromise;

                    if (!orderDossier.id) {
                        return acc;
                    }

                    try {
                        const action = selectOpenSearchDossier({
                            id: orderDossier.id,
                        }) as unknown as AnyAction;

                        const result = await dispatch(action);

                        if (result.payload) {
                            acc.push(result.payload as DossierOpenSearch);
                        } else {
                            // Fallback sur les données de base
                            acc.push({
                                id: orderDossier.id,
                                numero: orderDossier.numero,
                                societeDenomination: orderDossier.societeDenomination,
                                societeRef: orderDossier.societeRef,
                                dateCreation: orderDossier.dateCreation,
                                formalites: [],
                                validationProcesses: [],
                                stepLabel: '',
                                stepCompletion: 0,
                            } as DossierOpenSearch);
                        }
                    } catch (dossierError) {
                        console.warn(`Erreur pour le dossier ${orderDossier.id}:`, dossierError);
                        // Ajouter les données de fallback même en cas d'erreur
                        acc.push({
                            id: orderDossier.id,
                            numero: orderDossier.numero,
                            societeDenomination: orderDossier.societeDenomination,
                            societeRef: orderDossier.societeRef,
                            dateCreation: orderDossier.dateCreation,
                            formalites: [],
                            validationProcesses: [],
                            stepLabel: '',
                            stepCompletion: 0,
                            documentsCount: 0,
                        } as DossierOpenSearch);
                    }

                    return acc;
                },
                Promise.resolve([] as Array<DossierOpenSearch>),
            );

            setDossiers(fetchedDossiers);
        } catch (err) {
            console.error('Erreur lors du chargement des détails des dossiers:', err);
            setError('Erreur lors du chargement des détails des dossiers');

            // Fallback sur les données de base
            const fallbackDossiers = orderDossiers.map((orderDossier) => ({
                id: orderDossier.id,
                numero: orderDossier.numero,
                societeDenomination: orderDossier.societeDenomination,
                societeRef: orderDossier.societeRef,
                dateCreation: orderDossier.dateCreation,
                formalites: [],
                validationProcesses: [],
                stepLabel: '',
                stepCompletion: 0,
                documentsCount: 0,
            })) as Array<DossierOpenSearch>;

            setDossiers(fallbackDossiers);
        } finally {
            setLoading(false);
        }
    }, [orderDossiers, selectOpenSearchDossier, dispatch]);

    useEffect(() => {
        fetchDossierDetails();
    }, [fetchDossierDetails]);

    return { dossiers, loading, error, refetch: fetchDossierDetails };
};

const SelectedOrderProvider: React.FunctionComponent<PropsWithChildren> = function ({ children }) {
    const [selectedOrder, setSelectedOrder] = useState<Order>({
        dossiers: [],
        typeFacturation: 'SIMPLIFIEE',
    });
    const [isUpdatingPriority, setIsUpdatingPriority] = useState(false);

    const dispatch = useDispatch();
    const orderDataSource: DataSource<Order> = useSelector(ordersSelector);
    const { idOrder } = useParams();
    const { selectOrder, collectionApi } = useFormaliteService();
    const { enqueueSnackbar } = useSnackbar();

    // Utilisation du hook personnalisé pour récupérer les détails des dossiers
    const {
        dossiers,
        loading: dossiersLoading,
        error: dossiersError,
        refetch: refetchDossiers,
    } = useOrderDossiers(selectedOrder.dossiers);

    /**
     * Met à jour la priorité de la commande.
     *
     * Cette fonction :
     * 1. Effectue l'appel API pour mettre à jour la priorité de la commande
     * 2. Gère les états de chargement et d'erreur
     * 3. Synchronise les données après modification
     * 4. Recharge les dossiers associés pour refléter les changements
     *
     * @param {boolean} newCritical - La nouvelle valeur de priorité (true = urgent, false = normal)
     * @throws {Error} Si l'appel API échoue lors de la mise à jour
     */
    const updateOrderPriority = useCallback(
        async (newCritical: boolean) => {
            try {
                setIsUpdatingPriority(true);

                if (!selectedOrder?.id) {
                    console.error('ID de la commande manquant', {
                        orderId: selectedOrder?.id,
                    });
                    enqueueSnackbar('ID de la commande manquant', {
                        variant: 'error',
                    });
                    return;
                }

                // Appel API pour mettre à jour la priorité de la commande
                await collectionApi.apiCollectionsDossiersIdPatch({
                    id: selectedOrder.id.toString(),
                    collectionDeDossierCollectiondossierUpdate: {
                        urgent: newCritical,
                    },
                });

                // Mise à jour locale immédiate de l'état
                setSelectedOrder((prevOrder) => ({
                    ...prevOrder,
                    urgent: newCritical,
                }));

                // Message de succès
                enqueueSnackbar(
                    `Priorité de la commande modifiée en ${newCritical ? 'urgent' : 'normal'}`,
                    {
                        variant: 'success',
                    },
                );

                // Recharger les données pour refléter les changements côté serveur
                setTimeout(() => {
                    dispatch(
                        selectOrder({
                            id: selectedOrder.id as number,
                        }) as unknown as AnyAction,
                    );
                    refetchDossiers();
                    setIsUpdatingPriority(false);
                }, 500);
            } catch (error) {
                console.error(
                    'Erreur lors de la mise à jour de la priorité de la commande:',
                    error,
                );
                setIsUpdatingPriority(false);

                // Rollback de l'état local en cas d'erreur
                setSelectedOrder((prevOrder) => ({
                    ...prevOrder,
                    urgent: !newCritical, // Revenir à l'ancienne valeur
                }));

                enqueueSnackbar('Impossible de modifier la priorité de la commande', {
                    variant: 'error',
                });
            }
        },
        [selectedOrder, collectionApi, enqueueSnackbar, dispatch, refetchDossiers, selectOrder],
    );

    const refreshOrder = useCallback(() => {
        dispatch(
            selectOrder({
                id: idOrder as unknown as number,
            }) as unknown as AnyAction,
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (orderDataSource.selectedStatus === 'succeeded') {
            setSelectedOrder(orderDataSource.selected as Order);
        }

        return () => {};
    }, [orderDataSource]);

    useEffect(() => {
        if (!orderDataSource?.selected || orderDataSource?.selected?.id !== idOrder) {
            dispatch(
                selectOrder({
                    id: idOrder as unknown as number,
                }) as unknown as AnyAction,
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idOrder]);

    const contextValue = useMemo(
        () => ({
            order: selectedOrder,
            setOrder: setSelectedOrder,
            dossiers,
            setDossiers: () => {
                console.warn('setDossiers is managed internally. Use refreshOrder to reload data.');
            },
            refreshOrder: () => {
                refreshOrder();
                refetchDossiers();
            },
            dossiersLoading,
            dossiersError,
            isUpdatingPriority,
            updateOrderPriority,
        }),
        [
            selectedOrder,
            dossiers,
            dossiersLoading,
            dossiersError,
            isUpdatingPriority,
            updateOrderPriority,
            refreshOrder,
            refetchDossiers,
        ],
    );

    if (orderDataSource.selectedStatus !== 'succeeded') {
        return (
            <Box
                display="flex"
                justifyContent="center"
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginTop: '50px',
                }}
            >
                {orderDataSource.selectedStatus === 'loading' ||
                orderDataSource.selectedStatus === 'idle' ? (
                    <CircularProgress />
                ) : (
                    <p>Une erreur est survenue</p>
                )}
            </Box>
        );
    }

    return (
        <SelectedOrderContext.Provider value={contextValue}>
            {children}
        </SelectedOrderContext.Provider>
    );
};

export default SelectedOrderProvider;
